import { useState, useEffect, useCallback } from 'react'
import {
  Table,
  Button,
  Typography,
  Divider,
  Tag,
  Select,
  DatePicker,
  Modal,
  Form,
} from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  getDailyChallenges,
  getArenaGames,
  createDailyChallenge,
} from 'src/api/arena'
import PrivateImage from 'src/components/PrivateImage'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

const formatDate = (value?: string | number): string =>
  value ? moment(value).format('YYYY-MM-DD HH:mm') : '-'

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'green',
  DISABLED: 'red',
  FINISHED: 'default',
}

const STATUS_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Disabled', value: 'DISABLED' },
  { label: 'Finished', value: 'FINISHED' },
]

const CREATE_STATUS_OPTIONS: { label: string; value: ArenaDailyChallengeStatus }[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Disabled', value: 'DISABLED' },
  { label: 'Finished', value: 'FINISHED' },
]

interface CreateFormValues {
  gameId: string
  status: ArenaDailyChallengeStatus
  startAt: moment.Moment
  endAt: moment.Moment
}

interface AppliedFilters {
  status: string
  gameId: string
  startFrom?: string
  startTo?: string
}

interface GameOption {
  label: string
  value: string
}

const ArenaDailyChallenges = (): JSX.Element => {
  const navigate = useNavigate()
  const [challenges, setChallenges] = useState<DailyChallenge[]>([])
  const [total, setTotal] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)

  const [gameOptions, setGameOptions] = useState<GameOption[]>([])

  const [status, setStatus] = useState<string>('')
  const [gameId, setGameId] = useState<string>('')
  const [range, setRange] = useState<
    [moment.Moment | null, moment.Moment | null] | null
  >(null)

  const [applied, setApplied] = useState<AppliedFilters>({
    status: '',
    gameId: '',
  })

  useEffect(() => {
    const fetchGames = async (): Promise<void> => {
      const games = await getArenaGames()
      setGameOptions([
        { label: 'All Games', value: '' },
        ...games.map((g) => ({ label: g.title ?? g.gameId, value: g.gameId })),
      ])
    }
    fetchGames()
  }, [])

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const result = await getDailyChallenges({
        page: page - 1,
        size: pageSize,
        status: applied.status || undefined,
        gameId: applied.gameId || undefined,
        startFrom: applied.startFrom,
        startTo: applied.startTo,
      })
      setChallenges(result.data)
      setTotal(result.total)
    } catch (error) {
      console.error('Error fetching daily challenges:', error)
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, applied])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSearch = (): void => {
    setApplied({
      status,
      gameId,
      startFrom: range?.[0] ? range[0].startOf('day').toISOString() : undefined,
      startTo: range?.[1] ? range[1].endOf('day').toISOString() : undefined,
    })
    setPage(1)
  }

  const [createOpen, setCreateOpen] = useState<boolean>(false)
  const [creating, setCreating] = useState<boolean>(false)
  const [createForm] = Form.useForm()

  const formGameOptions = gameOptions.filter((option) => option.value !== '')

  const openCreate = (): void => {
    createForm.resetFields()
    setCreateOpen(true)
  }

  const handleCreate = async (): Promise<void> => {
    const values = (await createForm.validateFields()) as CreateFormValues
    setCreating(true)
    const result = await createDailyChallenge({
      gameId: values.gameId,
      status: values.status,
      startAt: values.startAt.toISOString(),
      endAt: values.endAt.toISOString(),
    })
    setCreating(false)
    if (result === 'success') {
      setCreateOpen(false)
      fetchData()
    }
  }


  const goToEdit = (challenge: DailyChallenge): void => {
    const params = new URLSearchParams()
    params.set('gameId', challenge.gameId ?? challenge.game?.gameId ?? '')
    params.set('title', challenge.game?.title ?? '')
    if (challenge.status) params.set('status', challenge.status)
    if (challenge.startAt) params.set('startAt', String(challenge.startAt))
    if (challenge.endAt) params.set('endAt', String(challenge.endAt))
    navigate(
      `/arena/daily-challenges/edit/${challenge.challengeId}?${params.toString()}`
    )
  }

  const columns: ColumnsType<DailyChallenge> = [
    {
      title: 'Game',
      key: 'game',
      render: (_: unknown, record: DailyChallenge) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PrivateImage
            src={record.game?.thumbnailS3Path}
            width={48}
            height={48}
          />
          <span>{record.game?.title ?? record.gameId ?? '-'}</span>
        </div>
      ),
    },
    {
      title: 'Start At',
      key: 'startAt',
      render: (_: unknown, record: DailyChallenge) => formatDate(record.startAt),
    },
    {
      title: 'End At',
      key: 'endAt',
      render: (_: unknown, record: DailyChallenge) => formatDate(record.endAt),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: DailyChallenge) => (
        <Tag color={STATUS_COLORS[record.status ?? ''] ?? 'default'}>
          {record.status ?? '-'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_: unknown, record: DailyChallenge) =>
        record.status === 'DISABLED' ? (
          <Button icon={<EditOutlined />} onClick={() => goToEdit(record)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  return (
    <div>
      <Title level={3}>
        <Text>Daily Challenges</Text>
      </Title>
      <Divider style={{ margin: '10px 0' }} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <Select
          style={{ width: 180 }}
          value={status}
          options={STATUS_OPTIONS}
          onChange={setStatus}
        />
        <Select
          style={{ width: 220 }}
          value={gameId}
          options={gameOptions}
          onChange={setGameId}
          showSearch
          optionFilterProp="label"
        />
        <RangePicker
        style={{ width: 220 }}
          value={range}
          onChange={(values) =>
            setRange(values as [moment.Moment | null, moment.Moment | null])
          }
        />
        <Button type="primary" style={{ width: 220 }} className="btn-primary" onClick={onSearch}>
          Search
        </Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button
          type="primary"
          className="btn-primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          Create Daily Challenge
        </Button>
      </div>

      <Table<DailyChallenge>
        rowKey="challengeId"
        loading={loading}
        columns={columns}
        dataSource={challenges}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage)
            setPageSize(nextSize)
          },
        }}
      />

      <Modal
        title="Create Daily Challenge"
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Create"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            label="Game"
            name="gameId"
            rules={[{ required: true, message: 'Please select a game' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select a game"
              options={formGameOptions}
            />
          </Form.Item>
          <Form.Item
            label="Start At"
            name="startAt"
            rules={[{ required: true, message: 'Please select a start date' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item
            label="End At"
            name="endAt"
            rules={[{ required: true, message: 'Please select an end date' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select a status" options={CREATE_STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

ArenaDailyChallenges.displayName = 'ArenaDailyChallenges'

export default ArenaDailyChallenges
